import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../../lib/utils.js";
import { MessageImagePreview } from "../MessageImagePreview/index.js";
import {
  INBOX_ATTACHMENT_IMAGE_SHELL_CLASS,
  INBOX_ATTACHMENT_IMAGE_THUMB_CLASS,
} from "../inbox-image-frame.js";
import { inboxImageLoadPhase } from "../message-image-preview-logic.js";
import { MessageMediaImageSkeleton } from "./MessageMediaImageSkeleton.js";

export type InboxImageLayout = "default" | "bubble";

/**
 * Inbox image thumbnail with stable frame, loading skeleton, and optional lightbox.
 * Provider CDN URLs (METADATA_ONLY) and R2 presigned URLs (STORED) share the same UX.
 */
export function MessageMediaImage({
  url,
  alt,
  className,
  fileName,
  mimeType,
  urlResolving = false,
  enablePreview = true,
  onRefreshUrl,
  ensureUrl,
  organizationId,
  attachmentId,
  layout = "default",
}: {
  url?: string;
  alt?: string;
  className?: string;
  fileName?: string | null;
  mimeType?: string | null;
  urlResolving?: boolean;
  enablePreview?: boolean;
  onRefreshUrl?: () => Promise<string | null>;
  ensureUrl?: () => Promise<string | null>;
  organizationId?: number;
  attachmentId?: string;
  layout?: InboxImageLayout;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [displayUrl, setDisplayUrl] = useState(url);

  useEffect(() => {
    setDisplayUrl(url);
    setImageLoaded(false);
    setFailed(false);
    setRetrying(false);
  }, [url]);

  const phase = inboxImageLoadPhase(displayUrl, urlResolving, imageLoaded, failed);
  const isBubble = layout === "bubble";

  const shellClass = cn(
    INBOX_ATTACHMENT_IMAGE_SHELL_CLASS,
    isBubble ? "rounded-[inherit]" : "rounded-md",
    !isBubble && "my-1",
    className,
  );

  const handleError = useCallback(async () => {
    if (retrying || !onRefreshUrl) {
      setFailed(true);
      return;
    }
    setRetrying(true);
    const freshUrl = await onRefreshUrl();
    setRetrying(false);
    if (freshUrl && freshUrl !== displayUrl) {
      setDisplayUrl(freshUrl);
      setImageLoaded(false);
      setFailed(false);
      return;
    }
    setFailed(true);
  }, [displayUrl, onRefreshUrl, retrying]);

  const openPreview = useCallback(async () => {
    if (!enablePreview || phase !== "loaded" || !displayUrl) {
      return;
    }
    if (ensureUrl) {
      const fresh = await ensureUrl();
      if (fresh) {
        setDisplayUrl(fresh);
      }
    }
    setPreviewOpen(true);
  }, [displayUrl, enablePreview, ensureUrl, phase]);

  if (phase === "error") {
    return (
      <div
        className={cn(
          shellClass,
          "flex items-center justify-center px-3 text-center",
        )}
      >
        <p className="m-0 text-[0.8125rem] text-ink-subtle">
          {t("inbox.media.unavailable", { defaultValue: "Media unavailable" })}
        </p>
      </div>
    );
  }

  if (phase === "resolving") {
    return (
      <MessageMediaImageSkeleton
        layout={layout}
        className={className}
        label={t("inbox.media.processing")}
      />
    );
  }

  return (
    <>
      <div className={cn(shellClass, "relative")}>
        {phase === "loading" ? (
          <MessageMediaImageSkeleton overlay layout={layout} label={t("inbox.media.processing")} />
        ) : null}
        <button
          type="button"
          disabled={phase !== "loaded"}
          className={cn(
            "relative z-[2] block border-0 bg-transparent p-0 text-left leading-none",
            phase === "loaded" ? "cursor-zoom-in" : "cursor-default",
            "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
            phase !== "loaded" && "pointer-events-none",
          )}
          onClick={() => void openPreview()}
          aria-label={t("inbox.media.preview.open")}
        >
          {displayUrl ? (
            <img
              src={displayUrl}
              alt={alt ?? ""}
              loading="lazy"
              decoding="async"
              onLoad={() => setImageLoaded(true)}
              onError={() => void handleError()}
              className={cn(
                INBOX_ATTACHMENT_IMAGE_THUMB_CLASS,
                isBubble ? "rounded-[inherit]" : "rounded-md",
                phase !== "loaded" && "opacity-0",
              )}
            />
          ) : null}
        </button>
      </div>
      {previewOpen && displayUrl ? (
        <MessageImagePreview
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          imageUrl={displayUrl}
          alt={alt ?? t("inbox.messageType.IMAGE")}
          fileName={fileName}
          mimeType={mimeType}
          organizationId={organizationId}
          attachmentId={attachmentId}
          ensureUrl={ensureUrl}
        />
      ) : null}
    </>
  );
}
